package com.klantenstop.web.pages.manageCompany.components

import androidx.compose.runtime.Composable
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.widgets.DsBorderRadius
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.FilledButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Composable
fun AddressInputSection(
    streetName: String,
    streetNumber: String,
    zipCode: String,
    city: String,
    country: String,
    onStreetNameChange: (String) -> Unit,
    onStreetNumberChange: (String) -> Unit,
    onZipCodeChange: (String) -> Unit,
    onCityChange: (String) -> Unit,
    onCountryChange: (String) -> Unit,
    onVerifyAddress: () -> Unit,
    isValidating: Boolean = false,
    validationResult: AddressValidationState? = null,
    hasAttemptedSave: Boolean = false,
    isAddressValidated: Boolean = false,
    modifier: Modifier = Modifier
) {

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.px)
    ) {
        // Street name + street number
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px)
        ) {
            DsEditableField(
                modifier = Modifier.fillMaxWidth(70.percent),
                id = "inputStreetName",
                label = "Straatnaam",
                placeholder = "Bondgenotenlaan",
                value = streetName,
                valid = (!hasAttemptedSave && validationResult !is AddressValidationState.Error) || streetName.isNotBlank(),
                onValueChange = onStreetNameChange,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )
            DsEditableField(
                modifier = Modifier.fillMaxWidth(30.percent),
                id = "inputStreetNumber",
                label = "Huis Nr.",
                placeholder = "123",
                value = streetNumber,
                valid = (!hasAttemptedSave && validationResult !is AddressValidationState.Error) || streetNumber.isNotBlank(),
                onValueChange = onStreetNumberChange,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )
        }

        // Zip code + city
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px)
        ) {
            DsEditableField(
                modifier = Modifier.fillMaxWidth(30.percent),
                id = "inputZipCode",
                label = "Postcode",
                placeholder = "3000",
                value = zipCode,
                valid = (!hasAttemptedSave && validationResult !is AddressValidationState.Error) || zipCode.isNotBlank(),
                onValueChange = onZipCodeChange,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )
            DsEditableField(
                modifier = Modifier.fillMaxWidth(70.percent),
                id = "inputCity",
                label = "Gemeente",
                placeholder = "Leuven",
                value = city,
                valid = (!hasAttemptedSave && validationResult !is AddressValidationState.Error) || city.isNotBlank(),
                onValueChange = onCityChange,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )
        }

        // Country
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "inputCountry",
            label = "Land",
            placeholder = "België",
            value = country,
            valid = (!hasAttemptedSave && validationResult !is AddressValidationState.Error) || country.isNotBlank(),
            onValueChange = onCountryChange,
            required = true,
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        // Verify button
        FilledButton(
            modifier = Modifier,
            borderRadius = DsBorderRadius(8.px),
            enabled = !isValidating,
            onClick = onVerifyAddress
        ) {
            Text(if (isValidating) "Verifiëren..." else "Verifieer Adres")
        }

        // Hint or warning about verification requirement
        when {
            hasAttemptedSave && !isAddressValidated && validationResult == null -> {
                VerifyAddressRequiredBanner()
            }

            validationResult == null -> {
                SpanText(
                    text = "Verifieer het adres via de knop hierboven. Dit is verplicht voor het opslaan.",
                    modifier = Modifier
                        .fontSize(12.px)
                        .color(MaterialColorVars.OnSurface.withAlpha(0.6f))
                )
            }
        }

        // Validation feedback
        validationResult?.let { result ->
            when (result) {
                is AddressValidationState.Success -> {
                    ValidationSuccessBanner()
                }

                is AddressValidationState.Error -> {
                    ValidationErrorBanner()
                }
            }
        }
    }
}

@Composable
private fun VerifyAddressRequiredBanner() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(12.px)
            .backgroundColor(MaterialColorVars.SecondaryContainer.value())
            .borderRadius(8.px)
            .border(1.px, LineStyle.Solid, MaterialColorVars.Secondary.value())
    ) {
        SpanText(
            text = "⚠ Adresverificatie vereist",
            modifier = Modifier
                .fontSize(14.px)
                .color(MaterialColorVars.OnSecondaryContainer.value())
        )
        SpanText(
            text = "Druk op 'Verifieer Adres' om het adres te bevestigen voordat je opslaat.",
            modifier = Modifier
                .padding(top = 4.px)
                .fontSize(12.px)
                .color(MaterialColorVars.OnSecondaryContainer.withAlpha(0.8f))
        )
    }
}

@Composable
private fun ValidationSuccessBanner() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(12.px)
            .backgroundColor(MaterialColorVars.PrimaryContainer.value())
            .borderRadius(8.px)
            .border(1.px, LineStyle.Solid, MaterialColorVars.Primary.value())
    ) {
        SpanText(
            text = "✓ Adres gevonden en bevestigd",
            modifier = Modifier
                .fontSize(14.px)
                .color(MaterialColorVars.OnPrimaryContainer.value())
        )
    }
}

@Composable
private fun ValidationErrorBanner() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(12.px)
            .backgroundColor(MaterialColorVars.ErrorContainer.value())
            .borderRadius(8.px)
            .border(1.px, LineStyle.Solid, MaterialColorVars.Error.value())
    ) {
        SpanText(
            text = "✗ Adres niet gevonden",
            modifier = Modifier
                .fontSize(14.px)
                .color(MaterialColorVars.OnErrorContainer.value())
        )
        SpanText(
            text = "Het opgegeven adres kon niet worden gevonden. Controleer de straatnaam, het huisnummer, de postcode en het land, en probeer opnieuw.",
            modifier = Modifier
                .padding(top = 4.px)
                .fontSize(12.px)
                .color(MaterialColorVars.OnErrorContainer.withAlpha(0.8f))
        )
    }
}

sealed class AddressValidationState {
    data object Success : AddressValidationState()
    data object Error : AddressValidationState()
}
